import { NODE_DIMENSIONS } from './layoutManager';

const NODE_TYPES = {
  DEFAULT: 'Default',
  END_CALL: 'End Call',
  TRANSFER_CALL: 'Transfer Call',
  KNOWLEDGE_BASE: 'Vector DB Knowledge Base',
  WEBHOOK: 'Webhook',
  TRANSFER_DIALOG_FLOW: 'Transfer Dialog Flow',
  CUSTOM_TOOL: 'Custom Tool',
  PRESS_BUTTON: 'Press Button',
  SCHEDULE_MEETING: 'Schedule Meeting',
  ROUTE: 'Route',
  WAIT_FOR_RESPONSE: 'Wait for Response'
};

const DEFAULT_MODEL_OPTIONS = {
  modelType: 'smart',
  temperature: 0.2,
  isSMSReturnNode: false,
  skipUserResponse: false,
  block_interruptions: false,
  disableSilenceRepeat: false
};

const DEFAULT_WARM_TRANSFER_FIELDS = {
  isEnabled: false,
  fromNumber: '',
  agentPrompt: '',
  holdMusicUrl: 'https://example.com/default-hold-music.mp3',
  userHandling: 'on-hold',
  waitingPrompt: '',
  optimizeForIVR: true,
  mergeCallPrompt: '',
  isAgentPromptStatic: false,
  useCustomFromNumber: false,
  isMergeCallPromptStatic: false
};

class DialogFlowPayloadManager {
  static getBaseNodeData(type = NODE_TYPES.DEFAULT) {
    const baseData = {
      type: type,
      isStart: false,
      modelOptions: { ...DEFAULT_MODEL_OPTIONS }
    };

    // Add type-specific fields based on node type
    if (type === NODE_TYPES.TRANSFER_CALL) {
      baseData.warmTransferFields = { ...DEFAULT_WARM_TRANSFER_FIELDS };
    }

    return baseData;
  }

  static formatNodeForApi(node) {
    // Remove visual/UI properties
    const { position, style, width, height, targetHandle, ...baseNode } = node;
    
    // Ensure proper type casing
    const formattedNode = {
      ...baseNode,
      type: node.data.type
    };

    // Format data object based on node type
    const data = { ...node.data };
    const nodeData = {
      type: data.type,
      name: data.name || 'Unnamed Node',
    };

    // Common fields for all node types - only include non-empty strings
    if (data.text?.trim()) nodeData.text = data.text;
    if (data.prompt?.trim()) nodeData.prompt = data.prompt;
    if (data.isStart === true) nodeData.isStart = true;
    if (data.isGlobal === true) {
      nodeData.isGlobal = true;
      if (data.globalLabel?.trim()) nodeData.globalLabel = data.globalLabel;
      if (data.globalPrompt?.trim()) nodeData.globalPrompt = data.globalPrompt;
    }
    if (data.condition?.trim()) nodeData.condition = data.condition;

    // Handle condition examples if they exist and have content
    if (data.conditionExamples?.length > 0) {
      const formattedExamples = data.conditionExamples
        .map(example => {
          const formattedExample = {};
          if (example["Condition Achieved"]) formattedExample["Condition Achieved"] = example["Condition Achieved"];
          if (example["Conversation History"]) formattedExample["Conversation History"] = example["Conversation History"];
          return formattedExample;
        })
        .filter(example => Object.keys(example).length > 0);
        
      if (formattedExamples.length > 0) {
        nodeData.conditionExamples = formattedExamples;
      }
    }

    // Common arrays for all node types - only include if they have content
    if (data.dialogueExamples?.length > 0) nodeData.dialogueExamples = data.dialogueExamples;
    if (data.pathwayExamples?.length > 0) nodeData.pathwayExamples = data.pathwayExamples;
    if (data.extractVars?.length > 0) nodeData.extractVars = data.extractVars;
    
    // Model options - only include fields with actual values
    if (data.modelOptions) {
      const cleanedModelOptions = {};
      const modelOptions = data.modelOptions;

      cleanedModelOptions.modelType = modelOptions.modelType;
      if (modelOptions.temperature !== null && modelOptions.temperature !== undefined) {
        cleanedModelOptions.temperature = modelOptions.temperature;
      }
      cleanedModelOptions.isSMSReturnNode = modelOptions.isSMSReturnNode || false;
      cleanedModelOptions.skipUserResponse = modelOptions.skipUserResponse || false;
      cleanedModelOptions.block_interruptions = modelOptions.block_interruptions || false;
      cleanedModelOptions.disableSilenceRepeat = modelOptions.disableSilenceRepeat || false;
      if (modelOptions.interruptionThreshold !== undefined && modelOptions.interruptionThreshold !== null) {
        cleanedModelOptions.interruptionThreshold = modelOptions.interruptionThreshold;
      }

      if (Object.keys(cleanedModelOptions).length > 0) {
        nodeData.modelOptions = cleanedModelOptions;
      }
    }

    // Type-specific fields
    switch (data.type) {
      case NODE_TYPES.END_CALL:
        // End Call nodes already have all needed fields from common fields
        break;

      case NODE_TYPES.TRANSFER_CALL:
        if (data.transferNumber?.trim()) nodeData.transferNumber = data.transferNumber;
        if (data.warmTransferFields) {
          const cleanedFields = {};
          Object.entries(data.warmTransferFields).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              cleanedFields[key] = value;
            }
          });
          if (Object.keys(cleanedFields).length > 0) {
            nodeData.warmTransferFields = cleanedFields;
          }
        }
        break;

      case NODE_TYPES.KNOWLEDGE_BASE:
        if (data.kbTool) nodeData.kbTool = data.kbTool;
        break;

      case NODE_TYPES.WEBHOOK:
        if (data.url) nodeData.url = data.url;
        if (data.method) nodeData.method = data.method;
        if (data.body && data.body !== '{}') nodeData.body = data.body;
        if (data.responseData?.length) nodeData.responseData = data.responseData;
        if (data.responsePathways?.length) nodeData.responsePathways = data.responsePathways;
        break;
    }

    formattedNode.data = nodeData;
    return formattedNode;
  }

  static formatEdgeForApi(edge) {
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.data?.label || edge.label || 'User Responded'
    };
  }

  static formatNodes(nodes) {
    return nodes.map((node, index) => {
      // Get the correct type, maintaining original casing
      let nodeType = node.type || node.data?.type || 'Default';
      
      // Convert kebab-case to proper case if needed
      if (nodeType.includes('-')) {
        nodeType = nodeType.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      // Create clean data object with only necessary fields
      const data = {
        type: nodeType,  // Ensure data.type matches the node type
        label: node.data?.name || 'Node',
        name: node.data?.name || 'Node',
      };

      // Only add fields if they exist
      if (node.data?.text?.trim()) data.text = node.data.text;
      if (node.data?.prompt?.trim()) data.prompt = node.data.prompt;
      if (node.data?.isStart === true) data.isStart = true;
      if (node.data?.isGlobal === true) {
        data.isGlobal = true;
        if (node.data?.globalLabel?.trim()) data.globalLabel = node.data.globalLabel;
        if (node.data?.globalPrompt?.trim()) data.globalPrompt = data.globalPrompt;
      }

      // Add type-specific fields
      if (nodeType === NODE_TYPES.TRANSFER_CALL) {
        if (node.data?.transferNumber) data.transferNumber = node.data.transferNumber;
        if (node.data?.warmTransferFields) data.warmTransferFields = node.data.warmTransferFields;
      }
      // Handle Vector DB Knowledge Base type
      if (nodeType === NODE_TYPES.KNOWLEDGE_BASE) {
        if (node.data?.kbTool) data.kbTool = node.data.kbTool;
      }
      
      return {
        id: node.id,
        type: nodeType,
        position: node.position || { 
          x: index * NODE_DIMENSIONS.width, 
          y: index * NODE_DIMENSIONS.height 
        },
        data,
        style: {
          width: NODE_DIMENSIONS.width,
          padding: '15px',
          borderRadius: '8px',
          cursor: 'pointer',
        },
        targetHandle: node.data?.isGlobal ? null : undefined,
      };
    });
  }

  static formatEdges(edges) {
    return edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'custom',
      data: {
        label: edge.data?.label || edge.label || 'User Responded'
      },
      style: {
        strokeWidth: 1.5,
        stroke: '#555',
      }
    }));
  }

  static createNodePayload({ id, type, position, data = {}, style, selected, dragging, positionAbsolute }) {

    // Ensure we're using the correct type format
    const nodeType = this.getNodeType(data.type);

    // Get base data with the correct type
    const baseData = this.getBaseNodeData(nodeType);

    // Merge the provided data with base data, ensuring type consistency
    const mergedData = {
      ...baseData,
      ...data,
      type: nodeType, // Ensure type is consistent
    };

    // Ensure modelOptions are properly merged
    mergedData.modelOptions = {
      ...DEFAULT_MODEL_OPTIONS,
      ...(data.modelOptions || {})
    };

    // For Transfer Call nodes, ensure warmTransferFields are properly merged
    if (nodeType === NODE_TYPES.TRANSFER_CALL) {
      mergedData.warmTransferFields = {
        ...DEFAULT_WARM_TRANSFER_FIELDS,
        ...(data.warmTransferFields || {})
      };
      mergedData.transferNumber = data.transferNumber || '';
    }

    // For Vector DB Knowledge Base nodes, ensure kbTool is properly handled
    if (nodeType === NODE_TYPES.KNOWLEDGE_BASE) {
      mergedData.kbTool = data.kbTool || '';
    }

    // Create the base payload
    const payload = {
      id,
      type: nodeType, // Top-level type matches the data type
      position: position || { x: 0, y: 0 },
      data: mergedData,
      style: style || {
        width: NODE_DIMENSIONS.width,
        padding: "15px",
        borderRadius: "8px",
        cursor: "pointer"
      },
      width: NODE_DIMENSIONS.width,
      height: NODE_DIMENSIONS.height
    };

    // Add optional fields
    if (selected !== undefined) payload.selected = selected;
    if (dragging !== undefined) payload.dragging = dragging;
    if (positionAbsolute) payload.positionAbsolute = positionAbsolute;

    return payload;
  }

  static getNodeType(type) {

    // If type is undefined or null, return default
    if (!type) {
      return NODE_TYPES.DEFAULT;
    }

    // If the type is already a valid NODE_TYPES value, return it
    if (Object.values(NODE_TYPES).includes(type)) {
      return type;
    }

    // Try to find a matching type (case-insensitive)
    const normalizedType = type.toLowerCase();
    const matchingType = Object.values(NODE_TYPES).find(
      validType => validType.toLowerCase() === normalizedType
    );

    return matchingType || NODE_TYPES.DEFAULT;
  }

  static getNodeTypes() {
    return Object.values(NODE_TYPES).map(value => ({
      value,
      label: value
    }));
  }

  static validateNodePayload(payload) {

    if (!payload) {
      return false;
    }

    const requiredFields = ['id', 'type', 'position', 'data'];
    const hasRequiredFields = requiredFields.every(field => field in payload);

    if (!hasRequiredFields) {
      return false;
    }

    // Validate type consistency
    const validType = this.getNodeType(payload.type);
    const isValid = payload.type === validType && payload.data.type === validType;

    return isValid;
  }
}

export {
  DialogFlowPayloadManager,
  NODE_TYPES,
  DEFAULT_WARM_TRANSFER_FIELDS,
  DEFAULT_MODEL_OPTIONS
}; 